import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { tenantUsersAPI, tenantsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2, KeyRound, UserPlus } from 'lucide-react';
import { formatDate } from '../lib/utils';

export default function MieterZugaenge() {
  const [tenantUsers, setTenantUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    tenant_id: '',
    email: '',
    password: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tuRes, tenantsRes] = await Promise.all([
        tenantUsersAPI.getAll(),
        tenantsAPI.getAll(),
      ]);
      setTenantUsers(tuRes.data);
      setTenants(tenantsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await tenantUsersAPI.create(formData);
      toast.success('Mieterzugang angelegt');
      setDialogOpen(false);
      setFormData({ tenant_id: '', email: '', password: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Anlegen');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await tenantUsersAPI.delete(deleteId);
      toast.success('Zugang gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleteId(null);
    }
  };

  // Tenants without a login yet
  const tenantsWithoutLogin = tenants.filter(
    (t) => t.is_active && !tenantUsers.some((tu) => tu.tenant_id === t.id)
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="mieter-zugaenge-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1
              className="text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Mieterzugänge
            </h1>
            <p className="text-slate-500 mt-1">
              Vergeben Sie Logins für Ihre Mieter, damit diese ihre Abrechnungen und Schadensmeldungen
              online verwalten können.
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-tenant-user-button"
                className="bg-slate-900 hover:bg-slate-800"
                disabled={tenantsWithoutLogin.length === 0}
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Zugang anlegen
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Neuen Mieterzugang anlegen</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="tenant_id">
                    Mieter <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.tenant_id}
                    onValueChange={(v) => setFormData({ ...formData, tenant_id: v })}
                    required
                  >
                    <SelectTrigger data-testid="tenant-user-tenant-select">
                      <SelectValue placeholder="Mieter auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {tenantsWithoutLogin.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">
                    E-Mail <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="tenant-user-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="mieter@email.de"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">
                    Passwort <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="password"
                    type="text"
                    data-testid="tenant-user-password-input"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Mindestens 6 Zeichen"
                    minLength={6}
                    required
                  />
                  <p className="text-xs text-slate-500">
                    Teilen Sie diese Zugangsdaten Ihrem Mieter sicher mit.
                  </p>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    data-testid="tenant-user-save-button"
                    className="bg-slate-900 hover:bg-slate-800"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Speichern...
                      </>
                    ) : (
                      'Anlegen'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {tenantUsers.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <KeyRound className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                Noch keine Mieterzugänge
              </h3>
              <p className="text-slate-500">
                Vergeben Sie Logins, damit Ihre Mieter ihre Abrechnungen einsehen können.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold">Mieter</TableHead>
                  <TableHead className="font-semibold">Einheit</TableHead>
                  <TableHead className="font-semibold">E-Mail</TableHead>
                  <TableHead className="font-semibold">Erstellt</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenantUsers.map((tu) => (
                  <TableRow key={tu.id} data-testid={`tenant-user-row-${tu.id}`}>
                    <TableCell className="font-medium text-slate-900">{tu.tenant_name}</TableCell>
                    <TableCell className="text-slate-600">{tu.unit_name}</TableCell>
                    <TableCell className="text-slate-600">{tu.email}</TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {formatDate(tu.created_at)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`delete-tenant-user-${tu.id}`}
                        onClick={() => setDeleteId(tu.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Zugang löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Der Mieter kann sich danach nicht mehr im Portal anmelden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
